"use client";

import Image from "next/image";
import {
  Bookmark,
  Clock,
  Globe,
  MapPin,
  Navigation,
  Phone,
  Search,
  Share2,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcSeoCtaRow } from "@/components/services/seo/CcSeoCtaRow";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { LsSubpageCopy } from "./ls-page-copy";

type MapCopy = LsSubpageCopy["map"];
type Business = MapCopy["businesses"][number];

const ACTION_ICONS = [Navigation, Bookmark, MapPin, Phone, Share2];
/** Map centre (% of the map) for the results list, so every pin is in view. */
const LIST_FOCUS = [63, 47];
/** Share of each star level in the example review summary. */
const REVIEW_BARS = [86, 9, 3, 1, 1];

function Stars({ value }: { value: string }) {
  const full = Math.round(Number(value.replace(",", ".")));
  return (
    <span className="cc-gm-stars" aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={12} strokeWidth={0} fill="currentColor" data-off={i >= full ? "true" : undefined} />
      ))}
    </span>
  );
}

/** Local pack order for a step: "us" climbs to `rank`, the others keep their order. */
function packRows(copy: MapCopy, rank: number) {
  const others = copy.businesses.filter((b) => b.id !== copy.us);
  const us = copy.businesses.find((b) => b.id === copy.us)!;
  const order = [...others];
  order.splice(Math.min(rank, others.length + 1) - 1, 0, us);
  return order;
}

/**
 * set.space-style scroll story in a realistic Google Maps window: the steps
 * on the left light up one at a time while the window moves from the
 * salon's business profile and reviews to the results list, where it climbs
 * to the top. The map is our own render of OpenStreetMap data for
 * Södermalm, with photo pins for each business.
 */
export function CcLsMap({ copy }: { copy: MapCopy }) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const steps = stepRefs.current.filter(Boolean) as HTMLLIElement[];
    if (!steps.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      // Phones keep the map window pinned on top, so the active line sits lower.
      { rootMargin: window.innerWidth < 810 ? "-74% 0px -16% 0px" : "-45% 0px -45% 0px" },
    );
    steps.forEach((step) => observer.observe(step));
    return () => observer.disconnect();
  }, []);

  const step = copy.steps[active];
  const us = copy.businesses.find((b) => b.id === copy.us)!;
  const order = packRows(copy, step.rank);
  const ui = copy.ui;
  const usStats = { rating: step.rating, count: step.count };
  const statsFor = (b: Business) => (b.id === copy.us ? usStats : { rating: b.rating, count: b.count });

  return (
    <section
      className="cc-section cc-ls-map"
      id="local-map"
      aria-labelledby="cc-ls-map-title"
      data-cc-nav="light"
      {...devSectionAttr("local-map")}
    >
      <div className="cc-ls-map__container">
        <header className="cc-ls-map__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-ls-map-title" className="cc-ls-map__title">
            {copy.title}
          </h2>
          <p className="cc-ls-map__intro">{copy.intro}</p>
        </header>

        <div className="cc-ls-map__grid">
          <ol className="cc-ls-map__steps">
            {copy.steps.map((item, index) => (
              <li
                key={item.id}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-index={index}
                className="cc-ls-map__step"
                data-active={index === active ? "true" : undefined}
              >
                <span className="cc-ls-map__step-num" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
            <li className="cc-ls-map__cta">
              <CcSeoCtaRow copy={{ primaryCta: copy.cta }} bookCallVariant="local-seo" />
              <small>{copy.ctaNote}</small>
            </li>
          </ol>

          <div className="cc-ls-map__sticky">
            <figure className="cc-gm" data-view={step.view}>
              <div className="cc-gm__map" aria-hidden="true">
                <div
                  className="cc-gm__canvas"
                  style={
                    {
                      "--ox": `${step.view === "list" ? LIST_FOCUS[0] : us.pin[0]}%`,
                      "--oy": `${step.view === "list" ? LIST_FOCUS[1] : us.pin[1]}%`,
                    } as CSSProperties
                  }
                >
                  <Image
                    src={copy.image.src}
                    alt=""
                    width={copy.image.width}
                    height={copy.image.height}
                    className="cc-gm__tiles"
                    unoptimized
                  />
                  {copy.labels.map((label) => (
                    <span
                      key={label.name}
                      className={`cc-gm-label cc-gm-label--${label.kind}`}
                      style={
                        {
                          "--x": `${label.x}%`,
                          "--y": `${label.y}%`,
                          "--a": `${label.angle}deg`,
                        } as CSSProperties
                      }
                    >
                      {label.name}
                    </span>
                  ))}
                  {copy.businesses.map((business) => {
                    const isUs = business.id === copy.us;
                    const rank = order.indexOf(business) + 1;
                    return (
                      <span
                        key={business.id}
                        className={`cc-gm-pin${isUs ? " cc-gm-pin--us" : ""}`}
                        data-top={isUs && step.rank === 1 ? "true" : undefined}
                        style={
                          {
                            "--x": `${business.pin[0]}%`,
                            "--y": `${business.pin[1]}%`,
                            "--d": `${rank * 0.06}s`,
                          } as CSSProperties
                        }
                      >
                        <span className="cc-gm-pin__photo">
                          <Image src={business.photo} alt="" width={96} height={96} sizes="56px" />
                        </span>
                        <span className="cc-gm-pin__name">
                          {business.name}
                          <small>
                            {statsFor(business).rating} <Star size={9} strokeWidth={0} fill="currentColor" />
                          </small>
                        </span>
                      </span>
                    );
                  })}
                </div>
                <span key={step.id} className="cc-gm__toast">
                  <span className="cc-gm__toast-dot" />
                  {step.badge}
                </span>
                <figcaption className="cc-gm__credit">{copy.attribution}</figcaption>
              </div>

              <div className="cc-gm__side" aria-hidden="true">
                <div className="cc-gm__search">
                  <Search size={16} strokeWidth={2.2} />
                  <span>{copy.query}</span>
                  <X size={16} strokeWidth={2.2} />
                </div>

                {/* Business profile: overview and reviews */}
                <div className="cc-gm__panel cc-gm__panel--place" data-on={step.view !== "list" ? "true" : undefined}>
                  <span className="cc-gm__cover">
                    <Image src={us.photo} alt="" width={1000} height={667} sizes="(min-width: 1100px) 340px, 90vw" />
                  </span>
                  <div className="cc-gm__head">
                    <strong>{us.name}</strong>
                    <span className="cc-gm__rating">
                      <b>{usStats.rating}</b>
                      <Stars value={usStats.rating} />
                      <span>({usStats.count})</span>
                    </span>
                    <small>{us.type}</small>
                  </div>
                  <div className="cc-gm__tabs">
                    {ui.tabs.map((tab, index) => (
                      <span
                        key={tab}
                        data-on={(step.view === "reviews" ? index === 1 : index === 0) ? "true" : undefined}
                      >
                        {tab}
                      </span>
                    ))}
                  </div>

                  <div className="cc-gm__place-body" data-on={step.view === "place" ? "true" : undefined}>
                    <div className="cc-gm__actions">
                      {ui.actions.map((label, index) => {
                        const Icon = ACTION_ICONS[index] ?? MapPin;
                        return (
                          <span key={label} className="cc-gm__action" data-primary={index === 0 ? "true" : undefined}>
                            <i>
                              <Icon size={16} strokeWidth={2.2} />
                            </i>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                    <ul className="cc-gm__info">
                      <li>
                        <MapPin size={16} strokeWidth={2} />
                        {us.address}, {copy.city}
                      </li>
                      <li>
                        <Clock size={16} strokeWidth={2} />
                        <span>
                          <b className="cc-gm__open">{ui.open}</b> · {ui.closes}
                        </span>
                      </li>
                      <li>
                        <Globe size={16} strokeWidth={2} />
                        {ui.website}
                      </li>
                      <li>
                        <Phone size={16} strokeWidth={2} />
                        {ui.phone}
                      </li>
                    </ul>
                  </div>

                  <div className="cc-gm__reviews-body" data-on={step.view === "reviews" ? "true" : undefined}>
                    <div className="cc-gm__summary">
                      <div className="cc-gm__bars">
                        {REVIEW_BARS.map((share, index) => (
                          <span key={share + "-" + index}>
                            <i>{5 - index}</i>
                            <span>
                              <span style={{ width: `${share}%` }} />
                            </span>
                          </span>
                        ))}
                      </div>
                      <div className="cc-gm__score">
                        <strong>{usStats.rating}</strong>
                        <Stars value={usStats.rating} />
                        <small>
                          {usStats.count} {ui.reviewsSummary}
                        </small>
                      </div>
                    </div>
                    <div className="cc-gm__review">
                      <span className="cc-gm__avatar">{ui.review.name.charAt(0)}</span>
                      <div>
                        <b>{ui.review.name}</b>
                        <span className="cc-gm__review-meta">
                          <Stars value="5" /> {ui.review.when}
                        </span>
                        <p>{ui.review.text}</p>
                        <p className="cc-gm__reply">{ui.review.reply}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results list */}
                <div className="cc-gm__panel cc-gm__panel--list" data-on={step.view === "list" ? "true" : undefined}>
                  <div className="cc-gm__chips">
                    <span>
                      <SlidersHorizontal size={13} strokeWidth={2.2} />
                    </span>
                    {ui.chips.map((chip) => (
                      <span key={chip}>{chip}</span>
                    ))}
                  </div>
                  <p className="cc-gm__results">{ui.results}</p>
                  <ul className="cc-gm__list">
                    {order.map((business, slot) => {
                      const isUs = business.id === copy.us;
                      const stats = statsFor(business);
                      return (
                        <li
                          key={business.id}
                          className={`cc-gm__row${isUs ? " cc-gm__row--us" : ""}`}
                          style={{ "--slot": slot } as CSSProperties}
                        >
                          <span className="cc-gm__row-text">
                            <strong>
                              {slot + 1}. {business.name}
                            </strong>
                            <span className="cc-gm__rating">
                              <b>{stats.rating}</b>
                              <Stars value={stats.rating} />
                              <span>({stats.count})</span>
                            </span>
                            <small>
                              {business.type} · {business.address}
                            </small>
                            <small>
                              <b className="cc-gm__open">{ui.open}</b> · {ui.closes}
                            </small>
                          </span>
                          <span className="cc-gm__thumb">
                            <Image src={business.photo} alt="" width={160} height={160} sizes="72px" />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </figure>
            <p className="cc-gm__example">{ui.example}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
